// Web-specific implementation of NovaVoiceModule using browser APIs

interface Listener {
  remove: () => void;
}

class WebAudioRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  /**
   * The most recent recording, kept as a Blob.
   *
   * Callers cannot recover it from the blob: URL: `fetch(blobUrl)` is a
   * connect-src request, which this app's CSP blocks, so the upload fails
   * before any network call is made. Handing back the Blob keeps the existing
   * string-based listener contract intact while giving the uploader something
   * it can actually put in a FormData.
   */
  private lastBlob: Blob | null = null;
  private audioChunks: Blob[] = [];
  private listeners: Set<(path: string) => void> = new Set();
  private recordingStartTime: number = 0;
  private maxRecordingDuration: number = 60000; // 60 seconds max

  async startRecording(): Promise<void> {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.error('Browser does not support audio recording');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Use webm format if available, fallback to other formats
      const mimeType = MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : 'audio/wav';

      this.mediaRecorder = new MediaRecorder(stream, { mimeType });
      this.audioChunks = [];
      this.recordingStartTime = Date.now();

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        const audioBlob = new Blob(this.audioChunks, { type: mimeType });
        this.lastBlob = audioBlob;
        const audioUrl = URL.createObjectURL(audioBlob);
        
        // Notify all listeners
        this.listeners.forEach(callback => callback(audioUrl));
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      this.mediaRecorder.start();

      // Auto-stop after max duration
      setTimeout(() => {
        if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
          this.stopRecording();
        }
      }, this.maxRecordingDuration);

    } catch (error) {
      console.error('Error starting recording:', error);
      throw error;
    }
  }

  getLastBlob(): Blob | null {
    return this.lastBlob;
  }

  async stopRecording(): Promise<string> {
    return new Promise((resolve) => {
      if (!this.mediaRecorder || this.mediaRecorder.state !== 'recording') {
        resolve('');
        return;
      }

      // Add a temporary listener to get the audio URL
      const tempCallback = (audioUrl: string) => {
        this.listeners.delete(tempCallback);
        resolve(audioUrl);
      };
      this.listeners.add(tempCallback);

      this.mediaRecorder.stop();
    });
  }

  addListener(callback: (path: string) => void): Listener {
    this.listeners.add(callback);
    
    return {
      remove: () => {
        this.listeners.delete(callback);
      }
    };
  }
}

class WebTTSModule {
  private synthesis: SpeechSynthesis | null = null;
  private currentUtterance: SpeechSynthesisUtterance | null = null;
  private cachedVoices: SpeechSynthesisVoice[] = [];

  constructor() {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      this.synthesis = window.speechSynthesis;

      // Voices load asynchronously; cache them when ready.
      // Also do an immediate load — some browsers populate synchronously.
      this.cachedVoices = this.synthesis.getVoices();
      this.synthesis.addEventListener('voiceschanged', () => {
        this.cachedVoices = this.synthesis?.getVoices() ?? [];
      });
    }
  }

  /**
   * Pick the best available English voice.
   *
   * Priority order (highest quality first):
   *  1. Cloud/neural voices (localService === false) with en-US locale
   *     – Chrome: "Google US English", Edge: "Microsoft …" voices
   *  2. Cloud/neural voices with en-GB locale
   *  3. Any cloud/neural voice with an en-* locale
   *  4. Local en-US voice
   *  5. Local en-GB voice
   *  6. Any local en-* voice
   */
  private pickEnglishVoice(): SpeechSynthesisVoice | null {
    const voices = this.cachedVoices.length
      ? this.cachedVoices
      : this.synthesis?.getVoices() ?? [];

    const cloud = voices.filter(v => !v.localService);
    const local = voices.filter(v => v.localService);

    return (
      cloud.find(v => v.lang === 'en-US') ??
      cloud.find(v => v.lang === 'en-GB') ??
      cloud.find(v => v.lang.startsWith('en')) ??
      local.find(v => v.lang === 'en-US') ??
      local.find(v => v.lang === 'en-GB') ??
      local.find(v => v.lang.startsWith('en')) ??
      null
    );
  }

  /**
   * Who wants to know while Nova is talking.
   *
   * Live dictation does: the microphone hears the speaker, transcribes Nova's
   * own answer, and — with submit-on-pause — asks it back as the next question.
   * The Web Speech API gives no way to apply echo cancellation to its own
   * capture, so the only reliable fix is to not listen while talking, which
   * needs these two moments.
   */
  private speakingListeners = new Set<(speaking: boolean) => void>();

  addSpeakingListener(cb: (speaking: boolean) => void): {remove: () => void} {
    this.speakingListeners.add(cb);
    return {remove: () => this.speakingListeners.delete(cb)};
  }

  private announce(speaking: boolean): void {
    this.speakingListeners.forEach(cb => {
      try {
        cb(speaking);
      } catch (e) {
        console.warn('Speaking listener failed:', e);
      }
    });
  }

  speak(text: string): void {
    if (!this.synthesis) {
      console.warn('Text-to-speech not supported in this browser');
      return;
    }

    // Cancel any ongoing speech.
    // Chrome has a known bug where calling speak() immediately after cancel()
    // causes the first word to be clipped. A short setTimeout works around it.
    this.synthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.95;   // slightly slower than default for clarity
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    const voice = this.pickEnglishVoice();
    if (voice) {
      utterance.voice = voice;
      utterance.lang = voice.lang;
    } else {
      utterance.lang = 'en-US';
    }

    utterance.onstart = () => this.announce(true);
    // `end` covers a finished utterance and a cancelled one; `error` covers a
    // voice that never started. Missing either would leave the microphone off
    // for good.
    utterance.onend = () => this.announce(false);
    utterance.onerror = () => this.announce(false);

    this.currentUtterance = utterance;

    // Delay by one event-loop tick so cancel() fully flushes before speak()
    setTimeout(() => {
      if (this.synthesis) {
        this.synthesis.speak(utterance);
      }
    }, 50);
  }

  stop(): void {
    // cancel() does not always fire onend for an utterance that never began.
    this.announce(false);
    if (this.synthesis) {
      this.synthesis.cancel();
    }
  }
}

// Create singleton instances
const audioRecorder = new WebAudioRecorder();
const ttsModule = new WebTTSModule();

export const NovaVoiceModule = {
  startListening: () => audioRecorder.startRecording(),
  stopListening: () => audioRecorder.stopRecording(),
  speak: (text: string) => ttsModule.speak(text),
  addListener: (cb: (path: string) => void) => audioRecorder.addListener(cb),
  /**
   * The last recording as a Blob — web only.
   *
   * Exists because the blob: URL the listeners receive cannot be read back:
   * `fetch()` on it is blocked by CSP connect-src. The native module has no
   * equivalent (there the path is a real file the uploader can reference), so
   * callers check for this before using it.
   */
  getLastRecordingBlob: (): Blob | null => audioRecorder.getLastBlob(),
  /** Stop speaking — what a barge-in does. */
  stopSpeaking: () => ttsModule.stop(),
  /**
   * Called with true when Nova starts talking and false when it stops, so a
   * caller can close the microphone while the speaker is live. Web only: the
   * native module owns its own audio session.
   */
  addSpeakingListener: (cb: (speaking: boolean) => void) =>
    ttsModule.addSpeakingListener(cb),
};